import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import { SelectChangeEvent } from "@mui/material/Select";
import {
  LocalizationProvider,
  MobileDateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ptBR } from "@mui/x-date-pickers/locales";
import dayjs, { Dayjs } from "dayjs";
import React from "react";
import { DiscountType } from "./controller";

export interface CreateCouponFormState {
  internalName: string;
  discountType: DiscountType;
  percentOff: string;
  amountOff: string;
  minOrderAmount: string;
  startsAt: string;
  endsAt: string;
  active: boolean;
}

interface CreateCouponDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  creating: boolean;
  error?: string;
  formValues: CreateCouponFormState;
  onChange: <K extends keyof CreateCouponFormState>(
    field: K,
    value: CreateCouponFormState[K]
  ) => void;
  onDiscountTypeChange: (event: SelectChangeEvent<DiscountType>) => void;
  title?: string;
  submitLabel?: string;
  submittingLabel?: string;
  disabled?: boolean;
  isLoading?: boolean;
}

const brazilianCurrencyFormatter = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const extractDigits = (value: string): string => value.replace(/\D/g, "");

const formatCurrencyFromDigits = (digits: string): string => {
  if (!digits) {
    return "";
  }
  const amount = Number(digits) / 100;
  return brazilianCurrencyFormatter.format(amount);
};

const formatCurrencyDisplay = (value: string): string => {
  const digits = extractDigits(value);
  if (!digits) {
    return "";
  }
  return `R$ ${formatCurrencyFromDigits(digits)}`;
};

const normalizeCurrencyValue = (value: string): string => {
  const digits = extractDigits(value);
  if (!digits) {
    return "";
  }
  return formatCurrencyFromDigits(digits);
};

export const CreateCouponDialog: React.FC<CreateCouponDialogProps> = ({
  open,
  onClose,
  onSubmit,
  creating,
  error,
  formValues,
  onChange,
  onDiscountTypeChange,
  title,
  submitLabel,
  submittingLabel,
  disabled,
  isLoading,
}) => (
  <Dialog
    open={open}
    onClose={creating ? undefined : onClose}
    fullWidth
    maxWidth="sm"
    variant="modal"
  >
    <DialogTitle variant="modal">
      <ConfirmationNumberIcon />
      {title ?? "Novo cupom"}
    </DialogTitle>
    <DialogContent variant="modal">
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={160}
        >
          <CircularProgress size={28} />
        </Box>
      ) : (
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          localeText={
            ptBR.components.MuiLocalizationProvider.defaultProps.localeText
          }
        >
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Nome interno"
              value={formValues.internalName}
              onChange={(event) => onChange("internalName", event.target.value)}
              fullWidth
              required
              disabled={creating || disabled}
            />

            <FormControl fullWidth disabled={creating || disabled}>
              <InputLabel id="discount-type-label">Tipo de desconto</InputLabel>
              <Select
                labelId="discount-type-label"
                label="Tipo de desconto"
                value={formValues.discountType}
                onChange={onDiscountTypeChange}
              >
                <MenuItem value="percent">Percentual</MenuItem>
                <MenuItem value="fixed_amount">Valor fixo</MenuItem>
              </Select>
            </FormControl>

            {formValues.discountType === "percent" ? (
              <TextField
                label="Percentual de desconto (%)"
                type="number"
                value={formValues.percentOff}
                onChange={(event) => onChange("percentOff", event.target.value)}
                fullWidth
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                required
                disabled={creating || disabled}
              />
            ) : (
              <TextField
                label="Valor de desconto (R$)"
                value={formatCurrencyDisplay(formValues.amountOff)}
                onChange={(event) =>
                  onChange(
                    "amountOff",
                    normalizeCurrencyValue(event.target.value)
                  )
                }
                fullWidth
                inputProps={{ inputMode: "numeric" }}
                required
                disabled={creating || disabled}
              />
            )}

            <TextField
              label="Compra mínima (R$)"
              value={formatCurrencyDisplay(formValues.minOrderAmount)}
              onChange={(event) =>
                onChange(
                  "minOrderAmount",
                  normalizeCurrencyValue(event.target.value)
                )
              }
              fullWidth
              inputProps={{ inputMode: "numeric" }}
              disabled={creating || disabled}
            />

            <MobileDateTimePicker
              label="Início da vigência"
              format="D/M/YYYY HH:mm"
              ampm={false}
              value={formValues.startsAt ? dayjs(formValues.startsAt) : null}
              referenceDate={dayjs().hour(0o0).minute(0o1)}
              onChange={(value: Dayjs | null) => {
                if (value && value.isValid()) {
                  onChange("startsAt", value.toISOString());
                } else {
                  onChange("startsAt", "");
                }
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  disabled: creating || disabled,
                },
                actionBar: {
                  actions: ["clear", "cancel", "accept"],
                },
              }}
            />

            <MobileDateTimePicker
              label="Fim da vigência"
              format="D/M/YYYY HH:mm"
              ampm={false}
              value={formValues.endsAt ? dayjs(formValues.endsAt) : null}
              referenceDate={dayjs().hour(23).minute(59)}
              onChange={(value: Dayjs | null) => {
                if (value && value.isValid()) {
                  onChange("endsAt", value.toISOString());
                } else {
                  onChange("endsAt", "");
                }
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  disabled: creating || disabled,
                },
                actionBar: {
                  actions: ["clear", "cancel", "accept"],
                },
              }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formValues.active}
                  onChange={(event) => onChange("active", event.target.checked)}
                  disabled={creating || disabled}
                />
              }
              label="Cupom ativo"
            />
          </Box>
        </LocalizationProvider>
      )}
    </DialogContent>
    <DialogActions variant="modal">
      <Button onClick={onClose} color="error" variant="outlined" disabled={creating}>
        Cancelar
      </Button>
      <Button
        onClick={onSubmit}
        variant="contained"
        color="primary"
        disabled={creating || disabled || isLoading}
      >
        {creating
          ? submittingLabel ?? "Salvando..."
          : submitLabel ?? "Criar cupom"}
      </Button>
    </DialogActions>
  </Dialog>
);
